/**
 * DefenderBot - Medium mode defender AI.
 * Stays in own half (z >= 0), marks player, tackles, and passes to striker.
 */

import * as BABYLON from "babylonjs";
import { Ball } from "./Ball";
import { Player } from "./Player";
import { BotBase } from "./BotBase";
import { AIRole, AIState, AllowedZone, SeparationConfig } from "../systems/DifficultyManager";
import { KickType } from "./HumanoidMesh";

// Defender-specific constants
const PASS_POWER = 8;
const PASS_COOLDOWN = 1.0;
const MARKING_DISTANCE = 5;
const INTERCEPT_DISTANCE = 8;

/**
 * Medium mode defender bot
 * - Zone restriction: own half only (z >= 0)
 * - States: DEFEND, TACKLE, PASS, RETURN_HOME
 * - Behavior: Mark player, tackle, pass to striker, return home
 */
export class DefenderBot extends BotBase {
  private _passTimer = 0;
  private _strikerPosition: BABYLON.Vector3 | null = null;

  constructor(
    scene: BABYLON.Scene,
    ball: Ball,
    homePosition: BABYLON.Vector3,
    allowedZone: AllowedZone,
    separationConfig: SeparationConfig,
    shadowGenerator?: BABYLON.ShadowGenerator,
    player?: Player
  ) {
    super(
      scene,
      ball,
      AIRole.DEFENDER,
      homePosition,
      allowedZone,
      separationConfig,
      shadowGenerator,
      player,
      "defenderBot"
    );
  }

  /**
   * Dark blue jersey color for defender
   */
  protected _getJerseyColor(): BABYLON.Color3 {
    return new BABYLON.Color3(0.0, 0.0, 0.7); // Dark blue jersey
  }

  /**
   * Set striker position reference for passing
   */
  setStrikerPosition(pos: BABYLON.Vector3): void {
    this._strikerPosition = pos;
  }

  /**
   * Update with pass timer
   */
  update(dt: number, teammates: BotBase[]): void {
    if (this._passTimer > 0) this._passTimer -= dt;
    super.update(dt, teammates);
  }

  /**
   * Defender-specific decision making
   */
  protected _makeDecision(
    distToBall: number,
    distToGoal: number,
    ballPos: BABYLON.Vector3,
    myPos: BABYLON.Vector3
  ): void {
    // Check if ball is in our zone (own half)
    const ballInOurZone = ballPos.z >= 0;

    // If ball is far away and not in our zone, return home
    if (!ballInOurZone && distToBall > 15) {
      this._state = AIState.RETURN_HOME;
      return;
    }

    // Tackle if player has ball and we're close
    if (this._player && this._player.hasBallPossession && distToBall < 2.5 && this._tackleCooldown <= 0) {
      this._state = AIState.TACKLE;
      return;
    }

    // If we have possession, look to pass to striker
    if (this.hasBallPossession && this._passTimer <= 0) {
      this._state = AIState.PASS;
      return;
    }

    // If we have possession but can't pass yet, dribble toward striker zone
    if (this.hasBallPossession) {
      this._state = AIState.DRIBBLE;
      return;
    }

    // If ball is in our zone, chase it
    if (ballInOurZone) {
      this._state = AIState.CHASE_BALL;
      return;
    }

    // Otherwise defend - position between ball and goal
    this._state = AIState.DEFEND;
  }

  /**
   * Handle state behavior with defender-specific states
   */
  protected _handleState(
    ballPos: BABYLON.Vector3,
    myPos: BABYLON.Vector3,
    distToBall: number,
    distToGoal: number,
    dt: number
  ): void {
    switch (this._state) {
      case AIState.DEFEND:
        this._handleDefense(ballPos, myPos, dt);
        break;
      case AIState.PASS:
        this._handlePass(ballPos, myPos);
        break;
      default:
        super._handleState(ballPos, myPos, distToBall, distToGoal, dt);
    }
  }

  /**
   * Defender-specific defense behavior
   * Position between ball/player and goal, mark the player
   */
  private _handleDefense(ballPos: BABYLON.Vector3, myPos: BABYLON.Vector3, dt: number): void {
    // Calculate defensive position - between ball and goal
    const goalPos = new BABYLON.Vector3(0, 0, 30); // Our goal (opponent scores here)
    
    // If player has ball, mark them
    let targetPos: BABYLON.Vector3;
    if (this._player && this._player.hasBallPossession) {
      // Position between player and goal
      const playerPos = this._player.mesh.position;
      const toGoal = goalPos.subtract(playerPos).normalize();
      targetPos = playerPos.add(toGoal.scale(3));
    } else {
      // Position between ball and goal
      const toGoal = goalPos.subtract(ballPos).normalize();
      targetPos = ballPos.add(toGoal.scale(5));
    }

    // Clamp target to our zone
    targetPos.z = Math.max(targetPos.z, this._allowedZone.minZ);
    targetPos.x = BABYLON.Scalar.Clamp(targetPos.x, this._allowedZone.minX, this._allowedZone.maxX);

    // Move toward defensive position
    const dx = targetPos.x - myPos.x;
    const dz = targetPos.z - myPos.z;
    const dist = Math.sqrt(dx * dx + dz * dz);

    if (dist > 0.5) {
      const speed = 5.5 * 0.8; // Slightly slower when positioning
      myPos.x += (dx / dist) * speed * dt;
      myPos.z += (dz / dist) * speed * dt;
      this._currentSpeed = speed;

      const angle = Math.atan2(dx, dz);
      this.mesh.rotation.y = BABYLON.Scalar.Lerp(this.mesh.rotation.y, angle, 0.1);
    } else {
      this._currentSpeed = 0;
      // Face the ball
      const toBall = ballPos.subtract(myPos);
      const angle = Math.atan2(toBall.x, toBall.z);
      this.mesh.rotation.y = BABYLON.Scalar.Lerp(this.mesh.rotation.y, angle, 0.1);
    }
  }

  /**
   * Pass to striker behavior
   */
  private _handlePass(ballPos: BABYLON.Vector3, myPos: BABYLON.Vector3): void {
    if (this._kickTimer > 0) return;

    // Find striker position (look for forward pass)
    let passTarget: BABYLON.Vector3;
    
    if (this._strikerPosition) {
      passTarget = this._strikerPosition.clone();
    } else {
      // Default pass target - striker zone
      passTarget = new BABYLON.Vector3(0, 0, -15);
    }

    // Calculate pass direction and power
    const dx = passTarget.x - myPos.x;
    const dz = passTarget.z - myPos.z;
    const dist = Math.sqrt(dx * dx + dz * dz);

    if (dist > 0.1) {
      const passDir = new BABYLON.Vector3(dx / dist, 0.1, dz / dist).normalize();
      this._ball.kick(passDir, PASS_POWER);
      
      this._kickTimer = PASS_COOLDOWN;
      this._passTimer = PASS_COOLDOWN;
      this.animator.triggerKick(KickType.Normal);
      this.hasBallPossession = false;
      this._state = AIState.DEFEND;
    }
  }

  /**
   * Reset pass timer on position reset
   */
  resetPosition(): void {
    super.resetPosition();
    this._passTimer = 0;
  }
}